import { UitgezetteDienst } from "@/entities/dossier-overzicht";
import { useLogboekStore } from "@/features/logboek/logboek-gelezen-status-store";
import { Dot, Separator, Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components";
import { cn, formatDateTimeRelative } from "@/shared/utils";
import { ReactNode } from "react";
import { Link, useParams } from "react-router";
import { usePrefetchDossier } from "../hooks";
import { Card } from "./ui";
import { UitgezetteDienstFactory } from "./uitgezette-diensten/uitgezette-diensten";

type DossierCardProps = {
    dossierId: string;
    seinStartDatum: Date;
    omschrijving: string;
    reikwijdteLabel: string;
    uitgezetteDiensten: UitgezetteDienst[];
    acties: number;
    suggesties: number;
    betrokkenTreinen: string[];
    linkPrefix?: string;
    isPinned?: boolean;
};

export function DossierCard({
    dossierId,
    seinStartDatum,
    omschrijving,
    reikwijdteLabel,
    uitgezetteDiensten,
    acties,
    suggesties,
    betrokkenTreinen,
    linkPrefix = "/dossier",
    isPinned = false,
}: Readonly<DossierCardProps>) {
    const { id: currentDossierId } = useParams() as { id: string };
    const isSelected = currentDossierId === dossierId;
    const time = formatDateTimeRelative(seinStartDatum);
    const markAsLaatstGelezen = useLogboekStore((s) => s.markAsLaatstGelezen);
    const prefetchDossierQueries = usePrefetchDossier(dossierId);

    return (
        <Card
            variant={isSelected ? "sein-selected" : "sein"}
            className={cn(isPinned && "border-l-content-emphasis border-l-2")}
            data-testid={`dossier-${dossierId}`}
            onMouseEnter={() => void prefetchDossierQueries()}
        >
            <Link
                className="flex grow cursor-default flex-col gap-1 py-1"
                to={!isSelected ? `${linkPrefix}/${dossierId}` : ""}
                aria-label={`Open dossier ${dossierId}`}
                title={`Open dossier ${dossierId}`}
                onClick={() => markAsLaatstGelezen(currentDossierId)}
            >
                <div className="leading-m text-content-emphasis flex w-full items-start justify-between px-2 text-xs font-medium">
                    <h2 title={reikwijdteLabel} className="shrink-1 truncate">
                        {reikwijdteLabel}
                    </h2>
                    <div className="flex flex-nowrap items-center gap-2 text-nowrap">
                        <time dateTime={seinStartDatum.toISOString()}>{time}</time>
                        {acties > 0 ? (
                            <Dot variant="error" size="m" data-testid="actie-indicator" />
                        ) : (
                            suggesties > 0 && <Dot variant="info" size="m" data-testid="suggestie-indicator" />
                        )}
                    </div>
                </div>
                <Separator orientation="horizontal" />
                <div className="text-s leading-m text-content-default flex w-full items-start justify-between gap-1 px-2">
                    <p title={omschrijving} className="truncate">
                        {omschrijving}
                    </p>
                </div>
                {/* empty title attribute suppresses the native html title from the parent link when hovering over uitgezetteDienst  */}
                <div className="flex justify-between gap-1.5 px-2">
                    <div className="flex gap-1.5" title="">
                        {uitgezetteDiensten.map((dienst) => (
                            <UitgezetteDienstFactory key={dienst.type} dienst={dienst} />
                        ))}
                    </div>
                    {betrokkenTreinen.length > 0 && (
                        <div title="">
                            {betrokkenTreinen.length === 1 ? (
                                <CustomBadge>{betrokkenTreinen[0]}</CustomBadge>
                            ) : (
                                <Tooltip>
                                    <TooltipTrigger>
                                        <CustomBadge multiple>{betrokkenTreinen[0]}</CustomBadge>
                                    </TooltipTrigger>
                                    <TooltipContent className="whitespace-pre-line">{betrokkenTreinen.join("\n")}</TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            </Link>
        </Card>
    );
}

type CustomBadgeProps = {
    multiple?: boolean;
    children: ReactNode;
};

const CustomBadge = ({ children, multiple = false }: CustomBadgeProps) => (
    <div
        className={cn("relative flex items-center justify-center align-baseline", multiple && "right-1.5")}
        data-testid="betrokkentrein-badge"
    >
        {multiple && <div className="border-emphasis bg-elevated-2 absolute top-0 left-1.5 h-full w-full rounded-full border" />}
        <div className="border-emphasis bg-elevated-2 relative flex items-center rounded-full border px-2 py-0.5 font-medium">
            <span className="text-content-subtle leading-l text-xs">{children}</span>
        </div>
    </div>
);
